import scopeAndSequence from '@/common/data/scopeAndSequence.json';
import Migrator from '@/common/types/migrations/migrator';
import { LevelId } from '@/common/types/scopeAndSequence';
import {
  AudioDelay,
  CharacterInventory,
  DailyActivitiesState,
  EquipmentConfiguration,
  GameProgress,
  GameSettings,
  LevelProgress,
  Student,
  TutorializationState,
} from '@/common/types/student';
import {
  defaultCharacterInventoryState,
  defaultDailyActivitiesState,
  defaultTutorializationState,
  generateStudentPassword,
  getInitialLevelProgress,
  initialGameSettings,
} from '@/common/utils/student';
import getStudentV10Level from '@/common/utils/studentV10LevelMap';

type StudentMigratorDeps = {
  findOrganisationId: (studentId: string) => Promise<string>;
};

// Declared early so migration functions can close over it.
// Assigned after all migration functions are defined.
let studentMigrator: Migrator<Student, StudentMigratorDeps>;

interface LegacyLevelProgress {
  levelId: number;
  completedPlaythroughs: number;
  [game: string]: number;
}

interface LegacyStudent {
  id: string;
  level: number;
  levelsProgress: LegacyLevelProgress[];
  createdCharacter?: CharacterInventory;
  placementTestId: string | null;
  password?: string;
}

type Student_v1 = LegacyStudent & { wordComplexity: string };
function addWordComplexity(old: any): Student_v1 {
  return {
    ...old,
    wordComplexity: 'mixed',
  };
}

type Student_v2 = Student_v1 & { wordLimit: number };
function addWordLimit(old: Student_v1): Student_v2 {
  return {
    ...old,
    wordLimit: 10,
  };
}

type Student_v3 = Student_v2 & { playPhonemeOnCardDrop: boolean };
function addPlayPhonemeOnCardDrop(old: Student_v2): Student_v3 {
  return {
    ...old,
    playPhonemeOnCardDrop: false,
  };
}

type Student_v4 = Student_v3 & { organisationId: string };
/* organisationId can no longer be found, but all students
   have org id now, so don't need this migration. */
// async function addOrganisationId(old: Student_v3): Promise<Student_v4> {
//   const findOrganisationId =
//     studentMigrator.requireAsyncDependency('findOrganisationId');
//   const organisationId = await findOrganisationId(old.id);
//   return {
//     ...old,
//     organisationId,
//   };
// }

interface V5LevelProgress {
  levelId: number;
  completedPlaythroughs: number;
  gamesProgress: Record<string, GameProgress>;
}
type Student_v5 = Omit<Student_v4, 'levelsProgress'> & {
  levelsProgress: V5LevelProgress[];
};
function restructureLevelsProgress(old: Student_v4): Student_v5 {
  const newLevelsProgress: V5LevelProgress[] = old.levelsProgress.map(
    ({ levelId, completedPlaythroughs, ...games }) => {
      const gamesProgress = Object.entries(games).reduce(
        (acc, [gameName, completions]) => {
          acc[gameName] = {
            timesPlayed: completions,
            timesMastered: completions,
            currentHighestMastery: completions > 0 ? 100 : 0,
            completedAtNodeIndex: -1,
          };
          return acc;
        },
        {} as Record<string, GameProgress>,
      );

      return {
        levelId,
        completedPlaythroughs,
        gamesProgress,
      };
    },
  );

  return { ...old, levelsProgress: newLevelsProgress };
}

type Student_v6 = Omit<
  Student_v5,
  'playPhonemeOnCardDrop' | 'wordLimit' | 'wordComplexity'
> & { settings: GameSettings };
function addNestedSettings(old: Student_v5): Student_v6 {
  // Remove old values
  const { playPhonemeOnCardDrop, wordLimit, wordComplexity, ...rest } = old;

  // Add settings
  return {
    ...rest,
    settings: {
      ...initialGameSettings,
      playPhonemeOnCardDrop: playPhonemeOnCardDrop ?? false,
      wordLimit: wordLimit ?? 10,
    },
  };
}

type Student_v7 = Student_v6 & { dailyActivities: DailyActivitiesState };
function addMoreSettingsAndDailyActivitiesState(old: Student_v6): Student_v7 {
  // Add daily activities state
  const updated: Student_v7 = {
    ...old,
    dailyActivities: defaultDailyActivitiesState,
  };

  // Add audio settings, duels enabled and distractors enabled
  if (!updated.settings) {
    updated.settings = initialGameSettings;
  } else if (!updated.settings.audio) {
    updated.settings.audio = {
      masterVolume: 100,
      sfxVolume: 100,
      instructionsVolume: 100,
      audioDelay: AudioDelay.SHORT,
      sfxOn: true,
      musicOn: true,
    };
  } else {
    updated.settings.audio.audioDelay = AudioDelay.SHORT;
  }
  updated.settings.duelsEnabled = true;
  updated.settings.distractorCardsEnabled = true;

  return updated;
}

type Student_v8 = Student_v7 & {
  mapsCompleted: number;
  lastMasteredGame: string | null;
  instructionsViewed: number[];
  visitedBiomes: number[];
};
function addVariousMinigameState(old: Student_v7): Student_v8 {
  return {
    ...old,
    mapsCompleted: 0,
    instructionsViewed: [],
    visitedBiomes: [],
    lastMasteredGame: null,
    dailyActivities: {
      ...old.dailyActivities,
      hasViewedDailyActivities: false,
    },
  };
}

type Student_v9 = Student_v8 & { lastLogin: number };
function addLastLogin(old: Student_v8): Student_v9 {
  return {
    ...old,
    lastLogin: 0,
  };
}

type Student_v10 = Omit<Student_v9, 'levelsProgress'> & {
  levelsProgress: LevelProgress[];
};
function updateScopeAndSequenceStructure(old: Student_v9): Student_v10 {
  return {
    ...old,
    levelsProgress: scopeAndSequence.levels.map((l) =>
      getInitialLevelProgress(l.id as LevelId),
    ),
    level: getStudentV10Level(old.level),
  };
}

type Student_v12 = Omit<Student_v10, 'createdCharacter'> & {
  characterInventory: CharacterInventory;
};
function replaceCreatedCharacterWithCharacterInventory(
  old: Student_v10,
): Student_v12 {
  const { createdCharacter, ...rest } = old;

  const oldEquipment =
    createdCharacter?.equippedItems ||
    defaultCharacterInventoryState.equippedItems;
  const newEquipment: EquipmentConfiguration = {
    hats: oldEquipment.hats || null,
    clothes: oldEquipment.clothes || 0,
    pants: oldEquipment.pants || 3,
    shoes: oldEquipment.shoes || 10,
    hair: oldEquipment.hair || 2,
    complexion: oldEquipment.complexion || 98,
    face: null,
    gloves: null,
    back: null,
  };

  // Ensure the player has all the default items unlocked.
  const unlockedItems = createdCharacter?.unlockedItems || [];
  for (const item of defaultCharacterInventoryState.unlockedItems) {
    if (!unlockedItems.includes(item)) unlockedItems.push(item);
  }

  return {
    ...rest,
    characterInventory: {
      coins: createdCharacter?.coins || defaultCharacterInventoryState.coins,
      newItems: [],
      equippedItems: newEquipment,
      unlockedItems,
    },
  };
}

type Student_v13 = Omit<Student_v12, 'instructionsViewed' | 'visitedBiomes'> & {
  tutorialization: TutorializationState;
};
function addTutorializationState(old: Student_v12): Student_v13 {
  const { instructionsViewed, visitedBiomes, ...rest } = old;
  return {
    ...rest,
    tutorialization: {
      ...defaultTutorializationState,
      instructionsViewed:
        instructionsViewed || defaultTutorializationState.instructionsViewed,
      biomesVisited: visitedBiomes || defaultTutorializationState.biomesVisited,
    },
  };
}

type Student_v14 = Student_v13;
function addWordSegmentationSetting(old: Student_v13): Student_v14 {
  return {
    ...old,
    settings: {
      ...old.settings,
      wordSegmentationEnabled: old.settings.wordSegmentationEnabled ?? false,
    },
  };
}

type Student_v15 = Student_v14;
function addArticulatoryGesturesSetting(old: Student_v14): Student_v15 {
  return {
    ...old,
    settings: {
      ...old.settings,
      articulatoryGesturesEnabled:
        old.settings.articulatoryGesturesEnabled ?? false,
    },
  };
}

type Student_v16 = Student_v15 & { password: string };
function addPassword(old: Student_v15): Student_v16 {
  return {
    ...old,
    password: old.password ?? generateStudentPassword(),
  };
}

type Student_v17 = Omit<Student_v16, 'placementTestId'> & {
  assessmentId: string | null;
};
function replacePlacementTestWithAssessment(old: Student_v16): Student_v17 {
  const { placementTestId, ...rest } = old;

  return { ...rest, assessmentId: null };
}

studentMigrator = new Migrator<Student, StudentMigratorDeps>({
  1: addWordComplexity,
  2: addWordLimit,
  3: addPlayPhonemeOnCardDrop,
  // Version 4 migration no longer needed. See note above.
  // 4: addOrganisationId,
  5: restructureLevelsProgress,
  6: addNestedSettings,
  7: addMoreSettingsAndDailyActivitiesState,
  8: addVariousMinigameState,
  9: addLastLogin,
  10: updateScopeAndSequenceStructure,
  // version 11 used to be a duplicate of the version 9 update,
  // leave a space here to avoid offsetting future versions
  12: replaceCreatedCharacterWithCharacterInventory,
  13: addTutorializationState,
  14: addWordSegmentationSetting,
  15: addArticulatoryGesturesSetting,
  16: addPassword,
  17: replacePlacementTestWithAssessment,
});

export default studentMigrator;
export const { migrate: migrateStudent, version: STUDENT_VERSION } =
  studentMigrator;
